import React, { useEffect, useState } from "react";
import DrawerCustom from "../../../../shared/general/Drawer";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, Grid, InputAdornment, MenuItem, styled, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Swal from 'sweetalert2'

import vendorService from "../../../../../../services/vendor.service";
import { getEmployeeEmployeeTypeChange, getEmployeeProfile, updateEmployeeEmployeeTypeChange } from "../../../../../../actions/employee";

const StyledRoot = styled(Box)({
  padding: "16px",
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      padding: "13.5px 14px",
      paddingRight: "32px",
      "& input": {
        padding: 0,
      },
    },
  },
});

const DrawerEditEmployeeTypeChange = (props) => {

  const { drawerConfig, handleClose } = props;

  const { result: userProfile } = useSelector((state) => state.userProfile);
  
  const [employeeTypeList, setEmployeeTypeList] = useState([]);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const validateYupSchema = yup.object({
    employeeType: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
    employeeId: yup.string().nullable().required(`${t("ThisFieldIsRequired")}`),
    start: yup.date().nullable().required(`${t("ThisFieldIsRequired")}`),
  });

  const useHookForm = useForm({
    defaultValues: {
      employeeType: null,
      employeeId: "",
      start: null,
      end: null,
      remark: "",
    },
    resolver: yupResolver(validateYupSchema),
    mode: "all",
  })

  const onSubmit = async (data) => {

    // console.log("data", data)

    const formData = {
      idEmployeeType: data.employeeType.idEmployeeType,
      employeeId: data.employeeId,
      start: dayjs(data.start).format("YYYY-MM-DD"),
      remark: data.remark || null,
    }

    const result = await dispatch(updateEmployeeEmployeeTypeChange(drawerConfig.data.idEmp, drawerConfig.data.idEmployeeEmployeeType, formData));

    if (result && result.status === 200) {
      handleClose();
      Swal.fire({
        title: t("DataSaveSuccessful"),
        icon: "success",
        timer: 2000,
        customClass: {
          container: "my-swal-container",
        }
      });
      dispatch(getEmployeeEmployeeTypeChange(drawerConfig.data.idEmp));
      dispatch(getEmployeeProfile(drawerConfig.data.idEmp));
    } else if (result && result.data && result.data.message) {
      Swal.fire({
        title: t(`${result.data.message}`),
        icon: "error",
        customClass: {
          container: "my-swal-container",
        }
      });
    } else {
      Swal.fire({
        title: t("AnErrorOccurred"),
        icon: "error",
        customClass: {
          container: "my-swal-container",
        }
      });
    }

  }

  useEffect(() => {
    if(drawerConfig.isOpen){

      vendorService.getEmployeeTypeByVendorId(userProfile.idVendor).then(res => {

        setEmployeeTypeList(res.data);

        const foundEmployeeType = res.data.find(x => x.idEmployeeType === drawerConfig.data.idEmployeeType);

        useHookForm.reset({
          employeeType: foundEmployeeType || null,
          employeeId: drawerConfig.data.employeeId || "",
          start: drawerConfig.data.start || null,
          end: drawerConfig.data.end || null,
          remark: drawerConfig.data.remark || "",
        })
      })

    }
  }, [drawerConfig.isOpen])

  return (
    <DrawerCustom
      title={t("EmployeeTypeEdit")}
      anchor={"right"}
      open={drawerConfig.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      <StyledRoot>
        <form onSubmit={useHookForm.handleSubmit(onSubmit)}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("EmployeeType")}</Typography>
                <Controller
                  name="employeeType"
                  control={useHookForm.control}
                  render={({field, fieldState}) => (
                    <Autocomplete
                      {...field}
                      options={employeeTypeList}
                      getOptionLabel={(option) => `${option.employeeTypeName}`}
                      isOptionEqualToValue={(option, value) => option.idEmployeeType === value.idEmployeeType}
                      renderOption={(props, option) => (
                        <MenuItem {...props} key={option.idEmployeeType}>
                          <Box>
                            <Typography>{option.employeeTypeName}</Typography>
                            <Typography color="text.secondary" fontSize="12px">{option.companyName}</Typography>
                          </Box>
                        </MenuItem>
                      )}
                      renderInput={(params) => (
                        <TextFieldTheme
                          {...params}
                          placeholder={t("SelectData")}
                          onBlur={field.onBlur}
                          error={fieldState.error? true: false}
                          helperText={fieldState.error? fieldState.error.message: null}
                        />
                      )}
                      value={field.value}
                      onChange={(_, value) => {
                        field.onChange(value)
                      }}
                      noOptionsText={t("NoData")}
                      disableClearable
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("EmployeeID")}</Typography>
                <Controller
                  name="employeeId"
                  control={useHookForm.control}
                  render={({field, fieldState}) => (
                    <TextFieldTheme
                      {...field}
                      onBlur={field.onBlur}
                      error={fieldState.error? true: false}
                      helperText={fieldState.error? fieldState.error.message: null}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("StartDate")}</Typography>
                <Controller
                  name="start"
                  control={useHookForm.control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      views={["year", "month", "day"]}
                      inputFormat="DD MMMM YYYY"
                      openTo="year"
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      renderInput={(params) => (
                        <TextFieldTheme
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: t("SelectDate"),
                            readOnly: true
                          }}
                          onBlur={field.onBlur}
                          error={fieldState.error? true : false}
                          helperText={fieldState.error? fieldState.error.message : null}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("Reason")}</Typography>
                <Controller
                  name="remark"
                  control={useHookForm.control}
                  render={({ field, fieldState }) => (
                    <TextFieldTheme
                      {...field}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              alignSelf: "flex-end",
                              fontSize: "12px",
                              userSelect: "none",
                            }}
                          >
                            {500 - field.value.length}
                          </InputAdornment>
                        ),
                      }}
                      multiline
                      minRows={3}
                      placeholder={`${t("Reason")}`}
                      error={fieldState.error? true: false}
                      helperText={fieldState.error? fieldState.error.message: null}
                      onChange={(event) => {
                        if (event.target.value.length > 500) {
                          field.onChange(event.target.value.slice(0, 500));
                        } else {
                          field.onChange(event.target.value);
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} container justifyContent="space-between">
                <ButtonBlue variant="text" onClick={handleClose} disabled={useHookForm.formState.isSubmitting}>{t("Cancel")}</ButtonBlue>
                <ButtonBlue variant="contained" type="submit" disabled={useHookForm.formState.isSubmitting}>{t("Save")}</ButtonBlue>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </form>
      </StyledRoot>
    </DrawerCustom>
  )
}

export default DrawerEditEmployeeTypeChange;